"use client";

import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, CalendarCheck, House as Home, Sparkles, ArrowRight, Phone } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const commitments = [{
  title: "Ground & property protection",
  description: "Protective sole pads, post caps, and careful tube handling keep your lawns, paving, and facade intact.",
  icon: ShieldCheck
}, {
  title: "Prompt strike and clearance",
  description: "We dismantle and remove all equipment on schedule as soon as your contractor or builder finishes.",
  icon: CalendarCheck
}, {
  title: "Courteous, verified crews",
  description: "Our teams hold NASC and CHAS safety standards, maintain clean accessways, and respect your neighbours.",
  icon: Home
}, {
  title: "Clear, fixed quotation",
  description: "Transparent pricing upfront based on your exact layout.",
  icon: Sparkles
}];
function ResidentialImage() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="w-full h-auto aspect-4/5 bg-muted animate-pulse" />;
  }
  const matchedPhoto = posts.find(post => post.customFields?.photo_name === "Homepage Residential Showcase Image" || (post as any).acf?.photo_name === "Homepage Residential Showcase Image" || post.title === "Homepage Residential Showcase Image");
  const rawImage = matchedPhoto?.customFields?.image || (matchedPhoto as any)?.acf?.image || matchedPhoto?.featuredImage;
  const imgSrc = typeof rawImage === "string" && rawImage.trim() !== "" ? rawImage : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/artifact_img_564uy3c0.png";
  const altText = matchedPhoto?.customFields?.alt_text || (matchedPhoto as any)?.acf?.alt_text || "Scaffolding erected on a residential house exterior with BMF Scaffolding branding banner";
  const isFallback = imgSrc === "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/artifact_img_564uy3c0.png";
  return <img src={imgSrc} data-wvc-srcset={isFallback ? "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/artifact_img_564uy3c0.png-wvc-srcset" : ""} data-wvc-sizes={isFallback ? "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/artifact_img_564uy3c0.png-wvc-sizes" : ""} alt={altText} loading="lazy" decoding="async" className="w-full h-auto aspect-4/5 object-cover" />;
}
export default function Residential() {
  const wp_query = new WP_Query({
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  });
  return <PostsProvider wp_query={{
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  }}>
      <section id="residential-capability" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-20 lg:py-28 border-b border-border overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Column: Authentic Project Photography & Key Metric */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.45,
            ease: [0.16, 1, 0.3, 1]
          }} className="lg:col-span-5 relative">
              <div className="relative rounded-lg overflow-hidden border border-border bg-muted shadow-md">
                <ResidentialImage />
                
                <div className="absolute inset-0 bg-linear-to-t from-secondary/80 via-secondary/20 to-transparent pointer-events-none" />

                {/* Verified Tag Overlay */}
                <div className="absolute bottom-5 left-5 right-5 p-4 rounded-md bg-card/95 backdrop-blur-xs border border-border shadow-xs text-card-foreground">
                  <div className="flex items-center gap-3">
                    <div className="size-10 rounded-sm bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                      <ShieldCheck className="size-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Domestic Safety Standard
                      </p>
                      <p className="text-sm font-bold text-foreground">
                        Family-run care, 100% certified safety
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Structural Accent Bar */}
              <div className="hidden sm:block absolute -top-3 -left-3 size-24 border-t-2 border-l-2 border-primary/40 rounded-tl-lg pointer-events-none" />
            </motion.div>

            {/* Right Column: Copy, Commitments & Action */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.45,
            delay: 0.1,
            ease: [0.16, 1, 0.3, 1]
          }} className="lg:col-span-7 flex flex-col justify-center">
              {/* Datum Rule & Category */}
              <div className="flex items-center gap-3 mb-4">
                <span className="h-0.5 w-8 bg-primary rounded-full" />
                <span className="text-xs sm:text-sm font-bold uppercase tracking-wider text-muted-foreground">
                  Residential & Domestic Scaffolding
                </span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default mb-4">
                Scaffolding built around your home.
              </h2>

              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-8 max-w-2xl">
                From roof replacements and extensions to exterior painting and solar setups, we provide safe access that treats your driveway, garden, and daily routine with care.
              </p>

              {/* Commitments Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
                {commitments.map((item, i) => {
                const IconComponent = item.icon;
                return <div key={item.title} data-index={i} className="p-4 rounded-md border border-border bg-card/60 hover:bg-card transition-[background-color,border-color] duration-200">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 size-8 rounded-sm bg-accent flex items-center justify-center shrink-0 text-foreground">
                          <IconComponent className="size-4 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-foreground mb-1">
                            {item.title}
                          </h3>
                          <p className="text-xs text-muted-foreground leading-normal">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </div>;
              })}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                <Button asChild size="lg" className="h-12 px-7 bg-primary text-primary-foreground font-semibold shadow-sm hover:bg-primary/90">
                  <Link to="/ResidentialScaffoldingPage">
                    Explore residential scaffolding
                    <ArrowRight className="size-4 ml-1.5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="h-12 px-6 border-border text-foreground hover:bg-accent font-medium">
                  <Link to="/ContactRequestQuotePage">
                    <Phone className="size-4 mr-2 text-muted-foreground" />
                    Request domestic quote
                  </Link>
                </Button>
              </div>
            </motion.div>

          </div>
        </div>
      </section>
    </PostsProvider>;
}